import type {
  EventSource,
  EventSourceType,
  IndexedEventSource,
} from "@/types/discover";

export type EventHostKind = "club" | "group" | "unknown";

export type EventHost = {
  kind: EventHostKind;
  /** Host kind narrowed to a valid analytics source type, or undefined when unknown. */
  sourceType: EventSourceType | undefined;
  displayName: string | null;
  clubId: string | null;
  groupId: string | null;
  canShowClubAffordances: boolean;
};

function present(value: string | undefined | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function resolveEventHost(event: EventSource): EventHost {
  const sourceType = event.source_type;
  const clubId = present(event.club_id);
  const clubName = present(event.club_name);
  const groupId = present(event.group_id);
  const groupName = present(event.group_name);

  if (sourceType === "group") {
    return {
      kind: "group",
      sourceType: "group",
      displayName: groupName,
      clubId: null,
      groupId,
      canShowClubAffordances: false,
    };
  }

  if (sourceType === "club" || clubId || clubName) {
    return {
      kind: "club",
      sourceType: "club",
      displayName: clubName,
      clubId,
      groupId: null,
      canShowClubAffordances: Boolean(clubId || clubName),
    };
  }

  return {
    kind: "unknown",
    sourceType: undefined,
    displayName: null,
    clubId: null,
    groupId: null,
    canShowClubAffordances: false,
  };
}

/**
 * Event reads that feed a page, an API route or a server action return this,
 * so contact details stay on the server. Only the contact route reads the
 * address.
 */
export function withoutContactDetails({
  contact_email,
  contact_phone_number: _phone,
  ...event
}: IndexedEventSource): EventSource {
  return { ...event, has_contact_email: Boolean(contact_email?.trim()) };
}

/** The event has a host the contact form can address. */
export function canContactEventHost(
  event: EventSource,
  host: EventHost,
): boolean {
  return event.has_contact_email === true && host.kind !== "unknown";
}

/**
 * Greeting name for the enquiry email. Uses host_display_name when it is
 * set, otherwise the club or group name on the event. Null when both are blank.
 */
export function greetingName(
  event: EventSource,
  host: EventHost,
): string | null {
  const line = (value: string | null | undefined) =>
    value?.replace(/\s+/g, " ").trim() ?? "";
  const name = line(event.host_display_name) || line(host.displayName);
  return name || null;
}

export function isClubBackedEvent(event: EventSource): boolean {
  return resolveEventHost(event).kind === "club";
}

export function isGroupBackedEvent(event: EventSource): boolean {
  return resolveEventHost(event).kind === "group";
}

/**
 * Public events are group-backed (`source_type === "group"`) — the discriminator
 * the signup funnel branches on. Domain-named alias over {@link isGroupBackedEvent}
 * so funnel code reads as intent ("is this a public event?") rather than infrastructure.
 */
export function isPublicEvent(event: EventSource): boolean {
  return isGroupBackedEvent(event);
}
